import {IncludedMeasure, Measure, MeasureType} from "../../services/GreenLoanService";
import {Card, CardContent, CardHeader, Checkbox, FormControlLabel, FormGroup, TextField} from "@mui/material";
import {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import {MeasureAmountInput} from "../MeasureAmountInput";
import {GreenLoanContext} from "../App";
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {Event} from "@mui/icons-material";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

interface Props {
    measure: Measure
    setIsOldMeasureChecked: Dispatch<SetStateAction<boolean>>
}

type FormValues = {
    implementationYear: number,
    isChecked: boolean
}

export function MeasureCardToggleOld(props: Props) {
    const {
        measure,
        setIsOldMeasureChecked
    } = props
    const {
        handleIncludedMeasureAdd,
        handleIncludedMeasureRemove,
        handleIncludedMeasureChange,
        projectInformation
    } = useContext(GreenLoanContext)
    const {currentYear} = useContext(GreenLoanContext)
    const cutoffYear = currentYear - 3;
    const hasBeenImplementedMaxYear = cutoffYear - 1
    const implementationYearMinValue = projectInformation.buildYear + 1
    const isOldImplementation = true
    const requiresPreviousImplementationYear: boolean = measure.requiresYearForOldImplementation && isOldImplementation

    const [measureInputValue, setMeasureInputValue] = useState(100)

    const schema = yup.object().shape({
        implementationYear: yup
            .number()
            .required()
            .min(implementationYearMinValue, `Må være større enn oppgitt byggeår (${projectInformation.buildYear})`)
            .max(hasBeenImplementedMaxYear, `Kan ikke være større enn ${hasBeenImplementedMaxYear}`),
        isChecked: yup.boolean().required().oneOf([true])
    })

    const {control, formState: {isValidating, isValid}, getValues} = useForm<FormValues>({
        mode: "onChange",
        resolver: yupResolver(schema),
        defaultValues: {
            implementationYear: hasBeenImplementedMaxYear,
            isChecked: false
        }
    })

    function getIncludedMeasure(): IncludedMeasure {
        return {
            measureNumber: measure.measureNumber,
            measureType: measure.type,
            percentage: measure.type === MeasureType.PERCENTAGE ? measureInputValue : undefined,
            amount: measure.type === MeasureType.AMOUNT ? measureInputValue : undefined,
            isOldImplementation: isOldImplementation,
            implementationYear: requiresPreviousImplementationYear ? getValues("implementationYear") : null
        }
    }

    useEffect(() => {
        if (isValid && !isValidating) {
            let includedMeasure = getIncludedMeasure();
            const successfulUpdate = handleIncludedMeasureChange(includedMeasure.measureNumber, includedMeasure)
            if (!successfulUpdate) {
                handleIncludedMeasureAdd(includedMeasure)
            }
            setIsOldMeasureChecked(true)
        }
        if (!isValid && isValidating) {
            handleIncludedMeasureRemove(measure.measureNumber, isOldImplementation)
            setIsOldMeasureChecked(false)
        }
    }, [isValidating, isValid, measureInputValue])

    const disableCheckBox: boolean = isOldImplementation && projectInformation.buildYear > hasBeenImplementedMaxYear

    return (
        <form>
            <Card sx={{
                boxShadow: "none"
            }}>
                <Controller name="isChecked"
                            control={control}
                            render={(({
                                          field: {value, onChange}
                                      }) =>
                                    <CardHeader
                                        style={{
                                            cursor: "pointer"
                                        }}
                                        onClick={() => {
                                            if (!disableCheckBox) {
                                                onChange(!value)
                                            }
                                        }}
                                        title={
                                            <FormGroup
                                                style={{
                                                    justifyContent: "space-between",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    pointerEvents: "none",
                                                    flexWrap: "nowrap"
                                                }}>
                                                <FormControlLabel
                                                    control={<Checkbox checked={value}
                                                                       disabled={disableCheckBox}
                                                                       sx={{color: 'primary'}}/>}
                                                    label={isOldImplementation ? `Gjort før ${cutoffYear}` : `Gjort siden ${cutoffYear}, eller skal gjøres`}
                                                />
                                            </FormGroup>
                                        }
                                    />
                            )}
                />
                {getValues("isChecked") && <CardContent>
                    {measure.type === MeasureType.AMOUNT &&
                        <MeasureAmountInput unit={measure.unit} value={measureInputValue}
                                            setValue={setMeasureInputValue}/>}

                    {requiresPreviousImplementationYear &&
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Controller
                                name="implementationYear"
                                control={control}
                                render={(({
                                              field: {value, onChange},
                                              fieldState: {error}
                                          }) =>
                                        <DatePicker label="Utført i (åååå)"
                                                    onChange={(value) => {
                                                        if (value) {
                                                            onChange(value.getFullYear())
                                                        }
                                                    }}
                                                    views={["year"]}
                                                    components={{
                                                        OpenPickerIcon: (props) => (
                                                            <Event {...props} color="primary"/>
                                                        ),
                                                    }}
                                                    value={new Date(value.toString())}
                                                    minDate={new Date((implementationYearMinValue).toString())}
                                                    maxDate={new Date((hasBeenImplementedMaxYear).toString())}
                                                    renderInput={(params) => {
                                                        return <TextField fullWidth={true}
                                                                          helperText={error?.message}
                                                                          error={!!error}
                                                                          {...params}/>
                                                    }}
                                        />
                                )}/>
                        </LocalizationProvider>}
                </CardContent>}
            </Card>
        </form>
    )
}